import React from 'react'
import Link from 'next/link'
import cx from 'classnames'
import { SubMenu } from '../SubMenu/SubMenu'
import styles from './NavMenu.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faChevronDown } from '@fortawesome/free-solid-svg-icons'
import CtaButton from '../CtaButton/CtaButton'
import LiveIcon from 'public/assets/live.svg'
import { useRouter } from 'next/router'
import { NavigationMenuItem } from '../../types'
import { ConditionalWrapper } from '~elements'
import { useNavigation } from '../../NavigationContext'

export const NavMenu = () => {
  const {
    ctaButton,
    endNavItem,
    fireAnalyticsClickEvent,
    isMobileView,
    navigationMenuItems,
    openSubMenuIndex,
    setOpenSubMenuIndex,
    toggleNavDrawer
  } = useNavigation()

  const { asPath } = useRouter()

  const toggleSubMenu = (index: number) => {
    setOpenSubMenuIndex(openSubMenuIndex === index ? null : index)
  }

  const isItemActive = (item: NavigationMenuItem) => {
    if (asPath === item.url) return true
    if (item.subNavLinks?.some(subNavLink => subNavLink.url === asPath)) {
      const currentChildPath = asPath.split('/')[1]?.toLowerCase()
      const parentSlug = item?.url && item.url.slice(1).toLowerCase()
      return currentChildPath === parentSlug
    }
    return false
  }

  return (
    <>
      <ul className={cx(styles['nav-list'], styles[`nav-list--${isMobileView ? 'mobile' : 'desktop'}`])}>
        {navigationMenuItems.map((item, index: number) => {
          const { isTargetBlank = false, subNavLinks, title, url } = item || {}
          const hasSubmenu = subNavLinks && subNavLinks.length > 0
          const active = isItemActive(item)

          if (!url || !title) return null

          return (
            <li
              key={item.title}
              className={cx([
                styles['nav-item'],
                styles[hasSubmenu ? 'nav-item--has-submenu' : 'nav-item--is-link'],
                openSubMenuIndex === index && styles['nav-item--submenu-is-open']
              ])}
              {...(!isMobileView &&
                hasSubmenu && {
                  onMouseEnter: () => setOpenSubMenuIndex(index),
                  onMouseLeave: () => setOpenSubMenuIndex(null)
                })}
            >
              <div
                className={styles['nav-item__title']}
                {...(isMobileView &&
                  hasSubmenu && {
                    onClick: () => toggleSubMenu(index)
                  })}
              >
                {/* Do not wrap parent nav-item title in Link if it has a Submenu */}
                <ConditionalWrapper
                  condition={!hasSubmenu}
                  wrapper={children => (
                    <Link
                      href={url}
                      target={isTargetBlank ? '_blank' : '_self'}
                      className={cx(active && styles['is-active'])}
                      onClick={() => fireAnalyticsClickEvent(url, title, (index + 1).toString())}
                    >
                      {children}
                    </Link>
                  )}
                >
                  <>{item.title}</>
                </ConditionalWrapper>
                {hasSubmenu && isMobileView && (
                  <div className={styles['caret']}>
                    <FontAwesomeIcon icon={faChevronDown} />
                  </div>
                )}
              </div>
              {subNavLinks && subNavLinks.length > 0 && <SubMenu item={item} parentIndex={index} />}
            </li>
          )
        })}
      </ul>

      <div className={styles['end-buttons']}>
        {endNavItem?.title && endNavItem?.url && (
          <Link
            href={endNavItem.url}
            className={styles['nav-item']}
            target='_self'
            {...(isMobileView && {
              onClick: () => toggleNavDrawer()
            })}
          >
            <LiveIcon />
            {endNavItem.title}
          </Link>
        )}
        {!isMobileView && <CtaButton {...ctaButton} />}
      </div>
    </>
  )
}
